import FilePreviewComponent from '@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue';
import RecursiveDependentQuestions from '@/components/probaworkflows_base_components/RecursiveDependentQuestions/RecursiveDependentQuestions.vue';
import {
  CLEAR_FORM_PROGRESS,
  GET_APPLICATION_FORMS,
  GET_FORM_PROGRESS,
  get_headers,
  PREVIEW_DOCUSEAL_URL,
  PREVIEW_ONBOARDING_URL,
  REQUEST_INFO_URL,
  SAVE_FORM_PROGRESS,
  SUBMIT_APPLICATION_FORMS_ANSWERS,
  VERIFY_SECTION_URL
} from '@/utils/constants/constants';
import alert_mixin from "@/utils/mixins/alert_mixin";
import entity_form_depquestions_mixins from '@/utils/mixins/entity_form_mixins/entity_form_depquestions_mixins';
import entity_form_files_mixin from '@/utils/mixins/entity_form_mixins/entity_form_files_mixin';
import entity_form_scroll_mixin from '@/utils/mixins/entity_form_mixins/entity_form_scroll_mixin';
import entity_form_utils_mixins from '@/utils/mixins/entity_form_mixins/entity_form_utils_mixins';
import save_sections from '@/utils/mixins/save_sections';
import {
  validate_non_empty_string,
  validate_non_sub_zero_number
} from '@/utils/validations/validate_inputs';
import FacialBiometric from "@/views/probaworkflows_base/verification/facial_biometric/FacialBiometric.vue";
import axios from 'axios';
import { reactive } from 'vue';

export default {
  name: "MultiSectionForm",
  mixins: [alert_mixin, save_sections, entity_form_scroll_mixin, entity_form_files_mixin, entity_form_depquestions_mixins, entity_form_utils_mixins],
  components: {
    FacialBiometric,
    FilePreviewComponent,
    RecursiveDependentQuestions
  },
  data: () => ({
    is_loading_form: false,
    preview_url: null,
    user_role: null,
    current_section_index: 0,
    completed_sections: [], // track saved sections
    headers: '',
    safe_form_config: {},
    safe_form_data: reactive({}),
    request_info_note: '',
    selected_request_fields: [],
    answered_file_questions: [],
    application_form_id: null,
    active_section: null,
    active_section_name: null,
    is_submitting: false,
    file_validation_error: {},
    validation_errors: {},
    show_file_preview_modal: false,
    preview_name: '',
    preview_type: 'other',
    preview_modal: false,
    submit_attempted: false,
    show_form_preview: false,
    form_preview_url: null,
    can_preview_submission: false,
    user_identity_defaults: {},
    current_form_type_name: '',
    inline_form_notice_text: '',
    inline_form_notice_type: 'info',
    show_profile_completion_gate: false,
    profile_completion_gate_message: '',
    expect_entity_form_after_profile: false,

  }),
  computed: {
    progress_percentage() {
      if (!this.safe_form_config.sections || this.safe_form_config.sections.length <= 1) return '0%';
      return `${((this.current_section_index + 1) / this.safe_form_config.sections.length) * 100}%`;
    },

    is_last_section() {
      if (!this.safe_form_config.sections?.length) return false;
      const lastIndex = this.safe_form_config.sections.length - 1;

      // fast path: index matches
      if (this.current_section_index === lastIndex) return true;

      // DOM fallback: check if the last section is mostly visible inside the carousel
      const container = this.$refs.carousel;
      if (!container) return false;
      const lastSection = container.querySelector(`.form-wrapper[data-index="${lastIndex}"]`);
      if (!lastSection) return false;

      const containerRect = container.getBoundingClientRect();
      const rect = lastSection.getBoundingClientRect();
      const isVertical = container.scrollHeight > container.clientHeight;

      const overlap = isVertical
        ? Math.max(0, Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top))
        : Math.max(0, Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left));

      const size = isVertical ? rect.height : rect.width;
      // require at least 50% visibility to count as "last"
      return size > 0 && (overlap / size) >= 0.5;
    }
  },
  mounted() {
    this.headers = get_headers();
    this.get_form_to_preview();
    this.application_form_id = Number(sessionStorage.getItem('application_form_id')) || null;

    // wait for DOM to be rendered
    this.$nextTick(() => {
      const carousel = this.$refs.carousel;
      if (!carousel) return;
      // add scroll listener with rAF debounce
      carousel.addEventListener('scroll', this._onCarouselScroll, { passive: true });
      // mobile touchend fallback after swipe
      carousel.addEventListener('touchend', this._onCarouselTouchEnd, { passive: true });
      // listen to resize (orientation change)
      window.addEventListener('resize', this._onWindowResize);
      // initial measurement
      this.handle_scroll();
    });
    // IntersectionObserver optional backup (kept for extra reliability)
    this.observe_sections();
  },
  beforeUnmount() {
    const carousel = this.$refs.carousel;
    if (carousel) {
      carousel.removeEventListener('scroll', this._onCarouselScroll);
      carousel.removeEventListener('touchend', this._onCarouselTouchEnd);
    }
    window.removeEventListener('resize', this._onWindowResize);
    // cancel any pending rAF
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
    if (this._touchTimeout) {
      clearTimeout(this._touchTimeout);
      this._touchTimeout = null;
    }

    if (this._observer) {
      try { this._observer.disconnect(); } catch (e) { /* ignore */ }
      this._observer = null;
    }
  },
  methods: {
    // Add this new method for clean exit
    exit_preview() {
      this.show_form_preview = false;
      this.current_section_index = this.safe_form_config.sections.length - 1;
      this.form_preview_url = null; // Clear the preview URL when exiting
    },
    generate_form_preview() {
      this.submit_attempted = true;

      // Validate entire form before preview
      let errors = {};
      this.safe_form_config.sections.forEach(section => {
        errors = { ...errors, ...this.validate_required_fields(section) };
      });

      this.validation_errors = errors;

      if (Object.keys(errors).length > 0) {
        this.show_info("Please complete all required fields before previewing");

        // Find the first section with errors and scroll to it
        const firstErrorSection = this.safe_form_config.sections.findIndex(section =>
          section.fields.some(field => this.validation_errors[field.model])
        );

        if (firstErrorSection !== -1) {
          this.current_section_index = firstErrorSection;
          this.$nextTick(() => {
            this.scroll_to_section_centered(firstErrorSection);
          });
        }

        return;
      }

      // Show loading state
      this.show_form_preview = true;
      this.form_preview_url = null;

      const request_payload = {
        application_form_id: this.application_form_id,
      };

      axios.post(
        PREVIEW_ONBOARDING_URL,
        request_payload,
        { headers: this.headers }
      )
      .then(res => {
        if (res.data.status === "success" && res.data.preview_url) {
          this.$nextTick(() => {
            this.form_preview_url = res.data.preview_url;

            this.$nextTick(() => {
              const previewElement = this.$el.querySelector('.form-wrapper:last-child');
              if (previewElement) {
                previewElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            });
          });
        } else {
          this.show_error("Unable to generate preview");
          this.exit_preview();
        }
      })
      .catch(() => {
        this.show_error("Error generating preview");
        this.exit_preview();
      });
    },
    on_update_field({ key, value }) {
      Reflect.set(this.safe_form_data, key, value);
    },
    handleNumberInput(event, field) {
      const value = event.target.value.replace(/\D/g, '')
      Reflect.set(this.safe_form_data, field.model, value)
      event.target.value = value
    },
    // Debounced scroll handler using rAF
    _onCarouselScroll() {
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = requestAnimationFrame(() => {
        this.handle_scroll();
        this._scrollRaf = null;
      });
    },

    // Touchend fallback: many mobile browsers complete the swipe after touchend
    _onCarouselTouchEnd() {
      if (this._touchTimeout) clearTimeout(this._touchTimeout);
      // short delay so the browser finishes inertia scroll
      this._touchTimeout = setTimeout(() => {
        this.handle_scroll();
        this._touchTimeout = null;
      }, 80);
    },

    _onWindowResize() {
      // re-evaluate visible section on resize/orientation change
      if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        this.handle_scroll();
        this._resizeTimeout = null;
      }, 120);
    },

    async save_progress_to_server() {
      await axios.post(SAVE_FORM_PROGRESS, {
        application_form_id: this.application_form_id,
        current_section_index: this.current_section_index
      }, {
        headers: this.headers
      });
    },

    async load_progress_from_server() {
      try {
        const response = await axios.get(GET_FORM_PROGRESS, {
          headers: this.headers,
          params: {
            application_form_id: this.application_form_id
          }
        });

        if (response.data.status === 'success' && response.data.current_section_index !== undefined) {
          const savedSectionIndex = response.data.current_section_index;

          if (savedSectionIndex >= 0 && savedSectionIndex < this.safe_form_config.sections.length) {
            this.current_section_index = savedSectionIndex;
            this.$nextTick(() => {
              this.scroll_to_section(savedSectionIndex);
            });
          }
        }
      } catch (error) {
        // Silently ignore errors - this is expected when user has no entity profile yet
      }
    },

    async clear_progress() {
      await axios.delete(CLEAR_FORM_PROGRESS, {
        headers: this.headers,
        data: {
          application_form_id: this.application_form_id
        }
      });
    },

    get_file_action_text(fileData) {
      return this.is_word_document(fileData) ? 'Download' : 'Preview';
    },

    guess_preview_type(filename = '', mime = '') {
      const name = String(filename || '').toLowerCase();
      const m = String(mime || '').toLowerCase();
      // Word documents
      if (m.includes('word') || /\.(doc|docx)$/.test(name)) return 'word';
      // Images
      if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
      // PDF
      if (m === 'application/pdf' || /\.pdf$/.test(name)) return 'pdf';
      // Videos
      if (m.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/.test(name)) return 'video';
      return 'other';
    },

    is_word_document(fileData) {
      if (!fileData) return false;
      const filename = fileData.file_name || '';
      const file = fileData.file;
      if (file instanceof File) {
        return this.guess_preview_type(file.name, file.type) === 'word';
      }
      return /\.(doc|docx)$/i.test(filename);
    },

    approve(section_id) {
      const data = {
        form_section_id: section_id,
        is_verified: true,
        application_form_id: parseInt(this.application_form_id, 10),
        verification_comment: "",
      };
      axios.post(VERIFY_SECTION_URL, data, {
        headers: this.headers
      })
        .then(response => {
          const response_data = response.data;
          if (response_data.status === 'success') {
            this.get_form_to_preview();
            this.show_success("Section verified successfully.")
          } else {
            this.show_error('Error during the form submit');
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.show_error(error.response.data.message);
          }
        });
    },

    toggle_requested_field(event, field) {
      if (event.target.checked) {
        this.selected_request_fields.push({
          form_section_id: this.active_section,
          form_section_name: this.active_section_name,
          requested_field_id: field.section_question_id,
          requested_field_text: field.question_text
        });
      } else {
        this.selected_request_fields = this.selected_request_fields.filter(
          selected => selected.requested_field_id !== field.section_question_id
        );
      }
    },

    async request_information() {
      if (!this.validate_request_information_inputs()) return;
      const application_form_id = parseInt(this.safe_form_config.formId, 10);
      const message = this.request_info_note;
      const requested_fields = this.selected_request_fields;
      const data = {
        application_form_id: application_form_id,
        message: message,
        requested_fields: requested_fields,
      };

      const response = await axios.post(REQUEST_INFO_URL, data, {
        headers: this.headers
      });

      if (response.data.status === 'success') {
        this.show_success("Information successfully requested");
      } else {
        this.show_error('Error during the form submit');
      }
    },

    validate_request_information_inputs() {
      if (!validate_non_empty_string(this.request_info_note)) {
        this.show_error('Request information note must not be null');
        return false;
      }
      if (!validate_non_sub_zero_number(parseInt(this.application_form_id, 10))) {
        this.show_error('Profile role form id must not be null');
        return false;
      }
      return true;
    },

    _init_dependent_form_data(dep_list = []) {
      (dep_list || []).filter(Boolean).forEach((dependent_question) => {
        Reflect.set(
          this.safe_form_data,
          dependent_question.model,
          dependent_question.type === "multi_select" ? (dependent_question.value || []) : (dependent_question.value || "")
        );

        if (dependent_question.type === "file") {
          const commentKey = dependent_question.model + "_file_comment";
          if (!Reflect.has(this.safe_form_data, commentKey)) {
            Reflect.set(this.safe_form_data, commentKey, "");
          }
        }

        if (dependent_question.dependent_questions?.length) {
          this._init_dependent_form_data(dependent_question.dependent_questions);
        }
      });
    },


    async get_form_to_preview() {
      let loader;
      try {
        loader = await this.show_loader("Loading form...");
        this.is_loading_form = true;

        const resp = await axios.get(GET_APPLICATION_FORMS, { headers: this.headers });

        // If no forms are assigned, redirect to view_all_user_forms page silently
        if (!resp.data.data || (Array.isArray(resp.data.data) && resp.data.data.length === 0)) {
          this.$router.push('/entity_overview'); //for everyone on uat
          return;
        }

        const payload = Array.isArray(resp.data.data)
          ? resp.data.data[0]
          : resp.data.data;

        this.application_form_id = payload.application_form_id;
        this.user_role = resp.data.logged_in_user_role;

        /* ---------------------------------------------------
        * Build lookup map once
        * --------------------------------------------------- */
        const all_questions_map = {};

        // Recursively index ALL questions (including nested dependants) into the map
        // so build_dependent_question can resolve them at any depth.
        const _index_questions = (dependent_question_list) => {
          (dependent_question_list || []).forEach(dependent_question => {
            if (!dependent_question) return;
            // A dependant question node on the raw API has dependant_section_question_id
            // as its own identity – index it under that id.
            if (dependent_question.dependant_section_question_id && !all_questions_map[dependent_question.dependant_section_question_id]) {
              // Synthesise a minimal record so _get_dep_value can work with it
              all_questions_map[dependent_question.dependant_section_question_id] = {
                section_question_id: dependent_question.dependant_section_question_id,
                question_text: dependent_question.dependant_question_text,
                question_type: dependent_question.dependant_question_type || { question_type: 'short_text' },
                answer: dependent_question.answer ?? null,
                multi_select_choices: dependent_question.multi_select_choices || [],
                is_mandatory: dependent_question.is_mandatory,
              };
            }
            // Keep descending
            _index_questions(dependent_question.dependant_questions || []);
          });
        };

        payload.form.sections.forEach(section =>
          section.questions.forEach(question => {
            Reflect.set(all_questions_map, question.section_question_id, question);
            // Also index every nested dependant question from this top-level question
            _index_questions(question.dependant_questions || []);
          })
        );

        /* ---------------------------------------------------
        * Build form config
        * --------------------------------------------------- */
        this.safe_form_config = {
          submission_id: payload.application_form_id,
          form_title: payload.form_name,
          formId: payload.form_id,
          sections: payload.form.sections
            .filter(section => section.questions?.length)
            .map(section => ({
              id: section.id,
              is_verified: section.is_verified,
              title: section.name,
              fields: section.questions
                .filter(q => !q.is_dependent)
                .map(q => {
                  const option_map = this.build_option_map(q.multi_select_choices);
                  const value = this.deserialize_answer(
                    q.answer,
                    q.question_type.question_type,
                    option_map
                  );

                  return {
                    label: q.question_text,
                    model: `q_${q.section_question_id}`,
                    id: `q_${q.section_question_id}`,
                    section_question_id: q.section_question_id,
                    type: this.map_question_type(q.question_type.question_type),
                    required: q.is_mandatory,
                    placeholder: q.question_text,
                    description: q.question_description,
                    options: (q.multi_select_choices || []).map(c => ({
                      text: c.choice,
                      value: c.choice
                    })),
                    value,
                    dependent_questions: (q.dependant_questions || [])
                      .map(dq => this.build_dependent_question(dq, all_questions_map))
                      .filter(Boolean),

                    is_dependent: false
                  };
                })
            }))
        };

        /* ---------------------------------------------------
        * Initialise form data once
        * --------------------------------------------------- */
        // clear existing reactive object without replacing it
        Object.keys(this.safe_form_data).forEach((key) => {
          Reflect.set(this.safe_form_data, key, undefined);
        });
        this.safe_form_config.sections.forEach(section =>
          section.fields.forEach(field => {
            Reflect.set(
              this.safe_form_data,
              field.model,
              field.type === 'multi_select' ? field.value || [] : field.value || ''
            );

            this._init_dependent_form_data(field.dependent_questions || []);
          })
        );

        this.$nextTick(() => {
          this.setup_dependent_question_listeners();
          this.load_progress_from_server();
        });

      } catch (error) {
        this.show_error("Error loading form");
      } finally {
        this.is_loading_form = false;
        if (loader) await this.close_loader(loader);
      }
    },

    async save_section() {
      if (this.is_submitting) return;
      this.submit_attempted = true;
      this.is_submitting = true;
      const config = this.safe_form_config;
      const form_data = new FormData();
      const answers = [];
      const file_uploads = [];
      const current_section = Reflect.get(config.sections, this.current_section_index);

      const errors = this.validate_required_fields(current_section);
      this.validation_errors = errors;
      if (Object.keys(errors).length > 0) {
        this.is_submitting = false;
        return;
      }

      this._collect_answers_recursive(current_section.fields, answers, file_uploads);

      file_uploads.forEach(fu => {
        if (fu && fu.id && fu.file) {
          const key = `file_${String(fu.id)}`;
          form_data.append(key, fu.file);
        }
      });

      form_data.append("answers", JSON.stringify(answers));
      form_data.append("application_form_id", this.application_form_id);
      form_data.append("current_section_index", this.current_section_index.toString());

      try {
        const res = await axios.post(
          SUBMIT_APPLICATION_FORMS_ANSWERS,
          form_data, { headers: { ...this.headers, "Content-Type": "multipart/form-data" } }
        );

        if (res.data && res.data.status === "success") {
          this.show_success("Section saved successfully");
          // Mark section as completed
          if (!this.completed_sections.includes(this.current_section_index)) {
            this.completed_sections.push(this.current_section_index);
          }
          // Save progress to server
          await this.save_progress_to_server();
          // Auto-scroll to next section if available
          if (this.current_section_index < this.safe_form_config.sections.length - 1) {
            this.current_section_index++;
            // Wait for DOM update then scroll to center the next section
            this.$nextTick(() => {
              this.scroll_to_section_centered(this.current_section_index);
            });
          }
        } else {
          const msg = (res.data && res.data.message) || "Section submission failed";
          this.show_error(msg);
        }
      } catch (error) {
        this.show_error("Error saving section");
      } finally {
        this.is_submitting = false;
      }
    },


    _collect_answers_recursive(nodes = [], answers = [], file_uploads = [], seen = new Set()) {
      nodes.forEach((node) => {
        if (!node) return;

        // skip hidden dependents
        if (node.is_dependent && !node.is_visible) return;

        // prevent duplicates + cycles
        const id = node.section_question_id;
        if (id && seen.has(id)) return;
        if (id) seen.add(id);

        // collect answer for this node
        this.process_field(node, answers, file_uploads);

        // recurse into children
        if (Array.isArray(node.dependent_questions) && node.dependent_questions.length) {
          this._collect_answers_recursive(node.dependent_questions, answers, file_uploads, seen);
        }
      });
    },

    preview_template() {
      const request_payload = {};

      axios.post(PREVIEW_DOCUSEAL_URL, request_payload, { headers: this.headers })
        .then(response => {
          const payload = response.data;

          if (payload.status !== "success") {
            this.show_error(payload.message || "Failed to generate preview");
            return;
          }

          this.preview_url = payload.embed_src;
        })
        .catch(error => {
          const error_message = error.response?.data?.message || error.message;
          this.show_error(error_message);
        })
        .finally(() => {
          this.is_preview_loading = false;
        });
    },


    // Make sure submit_answers also exits preview
    submit_answers() {
      if (this.is_submitting) return;

      let loader;

      this.show_loader("Submitting form...")
        .then(l => {
          loader = l;

          // Exit preview mode before submitting
          this.show_form_preview = false;

          // Validate all sections
          this.submit_attempted = true;
          let errors = {};
          this.safe_form_config.sections.forEach(section => {
            errors = { ...errors, ...this.validate_required_fields(section) };
          });
          this.validation_errors = errors;

          if (Object.keys(errors).length > 0) {
            this.is_submitting = false;
            this.show_info("Some mandatory fields were not filled in");
            return;
          }

          this.is_submitting = true;

          const config = this.safe_form_config;
          const form_data = new FormData();
          const answers = [];
          const file_uploads = [];

          config.sections.forEach(section => {
            this._collect_answers_recursive(section.fields, answers, file_uploads);
          });

          file_uploads.forEach(fu => form_data.append(`file_${fu.id}`, fu.file));
          form_data.append('answers', JSON.stringify(answers));
          form_data.append('application_form_id', config.submission_id);
          form_data.append('current_section_index', (config.sections.length - 1).toString());

          return axios.post(
            SUBMIT_APPLICATION_FORMS_ANSWERS,
            form_data,
            {
              headers: {
                ...this.headers,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        })
        .then(res => {
          if (res.data.status !== 'success') {
            this.show_error(res.data.message || "Form submission failed");
            return Promise.reject("Submission failed");
          } else {
            this.show_success('Form submitted successfully');

            return axios.post(
              SAVE_FORM_PROGRESS,
              {
                application_form_id: this.application_form_id,
                current_section_index: this.safe_form_config.sections.length - 1,
                mark_completed: true
              },
              {
                headers: this.headers
              }
            );
          }
        })
        .then(() => {
          this.$router.push('/entity_overview');
        })
        .catch(error => {
          if (error !== "Validation failed" && error !== "Submission failed") {
            this.show_error("Error submitting form");
          }
        })
        .finally(() => {
          this.is_submitting = false;
          if (loader) {
            this.close_loader(loader);
          }
        });
    },
  }
};